import { MutationCache, QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api.ts";
import { notify } from "./feedback.ts";
import { errorMessage } from "./error-message.ts";

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.meta?.feedback === false) return;
      notify({
        type: "error",
        title: "No pudimos guardar el cambio",
        description: errorMessage(
          error,
          "Revisá los datos e intentá nuevamente.",
        ),
      });
    },
    onSuccess: (_data, _variables, _context, mutation) => {
      if (mutation.options.meta?.feedback === false) return;
      notify({
        type: "success",
        title:
          typeof mutation.options.meta?.successMessage === "string"
            ? mutation.options.meta.successMessage
            : "Cambios guardados",
      });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // A 4xx won't fix itself — only retry infrastructure failures.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

export const ACTIVE_ORGANIZATION_KEY = "dd-organization-id";

export function activeOrganizationId(): string {
  return globalThis.localStorage?.getItem(ACTIVE_ORGANIZATION_KEY) ?? "none";
}

/**
 * Every server response scoped by the organization header must live below this
 * prefix. Clearing the cache on organization switch remains useful, but the
 * organization id in the key is the actual isolation boundary.
 */
export function tenantQueryKey(...parts: readonly unknown[]) {
  return ["organization", activeOrganizationId(), ...parts] as const;
}

/** Query keys in one place, so an invalidation can't drift from its query. */
export const keys = {
  /** Quién está usando el panel. Fuera de `organization`: no depende de cuál. */
  sessionUser: ["session-user"] as const,
  portalOverview: ["portal-overview"] as const,
  performedTreatments: (patientId: string) =>
    ["performed-treatments", patientId] as const,
  get organization() {
    return tenantQueryKey("status");
  },
  get organizationMembers() {
    return tenantQueryKey("members");
  },
  get subscription() {
    return tenantQueryKey("subscription");
  },
  /** Las sedes: bajo `organization`, que es de donde cuelgan. */
  get locations() {
    return tenantQueryKey("locations");
  },
  /** El panel de sedes: cuelga de las sedes, que es de lo que habla. */
  get sedePanel() {
    return tenantQueryKey("locations", "panel");
  },
  /** Las cajas de una sede, o de todas cuando no se filtra. */
  cashSessions: (locationId?: string) =>
    tenantQueryKey("cash", locationId ?? "all"),
  payouts: (filters?: unknown) => tenantQueryKey("payouts", filters ?? {}),
  auditEvents: (filters?: unknown) =>
    tenantQueryKey("audit-events", filters ?? {}),
  get inventory() {
    return tenantQueryKey("inventory");
  },
  inventoryLots: (itemId: string) =>
    tenantQueryKey("inventory", itemId, "lots"),
  inventoryTrace: (itemId: string) =>
    tenantQueryKey("inventory", itemId, "trace"),
  /** El catálogo de tratamientos: junto al inventario, que es donde se ve. */
  get treatments() {
    return tenantQueryKey("inventory", "treatments");
  },
  get finances() {
    return tenantQueryKey("finances");
  },
  get financeCategories() {
    return tenantQueryKey("finances", "categories");
  },
  // Nested under `finances` on purpose: booking an income invalidates the
  // ledger and the per-patient totals derived from it in one call.
  get patientSpend() {
    return tenantQueryKey("finances", "by-patient");
  },
  get surveys() {
    return tenantQueryKey("surveys");
  },
  surveysFor: (locationId?: string | null) =>
    tenantQueryKey("surveys", locationId ?? "all"),
  /** El prefijo: invalidar con esto alcanza al tablero de todas las sedes. */
  get dashboard() {
    return tenantQueryKey("dashboard");
  },
  get notifications() {
    return tenantQueryKey("notifications");
  },
  // La sede entra en la clave: sin esto, cambiar de sede seguiría mostrando
  // los números de la anterior hasta que la caché se venciera sola. Va como
  // sufijo del prefijo de arriba para que las invalidaciones que ya existen
  // —cargar un paciente, mover un turno— sigan alcanzándolas a todas.
  dashboardFor: (locationId?: string | null) =>
    tenantQueryKey("dashboard", locationId ?? "all"),
  patients: (search?: string, locationId?: string | null) =>
    tenantQueryKey("patients", locationId ?? "all", search ?? ""),
  get patientLists() {
    return tenantQueryKey("patients");
  },
  patient: (id: string) => tenantQueryKey("patients", "detail", id),
  patientRelationships: (id: string) =>
    tenantQueryKey("patients", "relationships", id),
  get retentionPolicy() {
    return tenantQueryKey("retention", "policy");
  },
  get securityPolicy() {
    return tenantQueryKey("security", "policy");
  },
  get communications() {
    return tenantQueryKey("communications");
  },
  get inactivePatients() {
    return tenantQueryKey("retention", "patients");
  },
  retentionReport: (id: string) => tenantQueryKey("retention", "report", id),
  // Bajo `patients` para que al escribir una nota se refresque el historial de
  // esa paciente sin invalidar la agenda entera.
  patientNotes: (id: string) => tenantQueryKey("patients", "notes", id),
  // Bajo `patients` para que firmar una consulta o cobrar un turno refresque
  // la cronología de esa paciente y nada más.
  patientTimeline: (id: string, filters?: unknown) =>
    tenantQueryKey("patients", "timeline", id, filters ?? {}),
  patientTimelineSummary: (id: string) =>
    tenantQueryKey("patients", "timeline-summary", id),
  tasks: (filters?: unknown) => tenantQueryKey("tasks", filters ?? {}),
  get taskLists() {
    return tenantQueryKey("tasks");
  },
  task: (id: string) => tenantQueryKey("tasks", "detail", id),
  waitingRoom: (params?: unknown) =>
    tenantQueryKey("waiting-room", params ?? {}),
  get waitingRoomBoards() {
    return tenantQueryKey("waiting-room");
  },
  patientEncounters: (id: string) =>
    tenantQueryKey("patients", "encounters", id),
  encounter: (id: string) => tenantQueryKey("encounters", id),
  patientPhotos: (id: string) => tenantQueryKey("patients", "photos", id),
  /** Órdenes, resultados e indicaciones de una paciente. */
  patientOrders: (id: string) => tenantQueryKey("patients", "orders", id),
  patientInstructions: (id: string) =>
    tenantQueryKey("patients", "instructions", id),
  /** La bandeja de la clínica: lo que llegó y nadie miró. */
  get clinicalInbox() {
    return tenantQueryKey("clinical-orders", "inbox");
  },
  /** Signos vitales: bajo `patients`, se invalidan con la ficha. */
  patientVitals: (id: string) => tenantQueryKey("patients", "vitals", id),
  patientVitalSeries: (id: string, type: string) =>
    tenantQueryKey("patients", "vitals", id, type),
  /** La lista de problemas: bajo `patients`, se invalida con la ficha. */
  patientProblems: (id: string) => tenantQueryKey("patients", "problems", id),
  encounterDiagnoses: (id: string) =>
    tenantQueryKey("encounters", id, "diagnoses"),
  /** Alergias y medicación: bajo `patients`, se invalidan con la ficha. */
  patientClinicalProfile: (id: string) =>
    tenantQueryKey("patients", "clinical-profile", id),
  patientConsents: (id: string) => tenantQueryKey("patients", "consents", id),
  appointments: (params?: unknown) =>
    tenantQueryKey("appointments", params ?? {}),
  /** Toda la agenda, sin importar el rango: para invalidar de una vez. */
  get appointmentLists() {
    return tenantQueryKey("appointments");
  },
  resources: (params?: unknown) => tenantQueryKey("resources", params ?? {}),
  /** Campañas y su retorno. Bajo `finances`: es dinero y se invalida con él. */
  get campaigns() {
    return tenantQueryKey("finances", "campaigns");
  },
  /** El padrón de profesionales: lo lee la agenda y lo edita Organización. */
  get team() {
    return tenantQueryKey("team");
  },
  teamFor: (locationId?: string | null) =>
    tenantQueryKey("team", "scope", locationId ?? "all"),
  professional: (id: string) => tenantQueryKey("team", "detail", id),
  professionalActivity: (id: string) =>
    tenantQueryKey("team", "detail", id, "activity"),
  appointmentPreconsultations: (id: string) =>
    tenantQueryKey("appointments", id, "preconsultations"),
  professionalMetrics: (id: string, filters: unknown) =>
    tenantQueryKey("team", "detail", id, "metrics", filters),
  professionalHistory: (id: string) =>
    tenantQueryKey("team", "detail", id, "history"),
  professionalCapacity: (filters: unknown) =>
    tenantQueryKey("team", "capacity", filters),
  get preconsultationTemplates() {
    return tenantQueryKey("preconsultations", "templates");
  },
  get portalPreconsultations() {
    return ["portal", "preconsultations"] as const;
  },
  portalPreconsultation: (id: string) =>
    ["portal", "preconsultations", id] as const,
};
